import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  MessageSquare, Send, ClipboardCheck, X, Save, ChevronLeft,
  ShieldCheck, Users, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { formatDistanceToNow, parseISO, format } from "date-fns";

const PRIORITY_STYLE = {
  low:    { badge: "bg-gray-100 text-gray-600", label: "Low" },
  normal: { badge: "bg-[#DBEAFE] text-[#1D4ED8]", label: "Normal" },
  high:   { badge: "bg-[#FEE2E2] text-[#B91C1C]", label: "High" },
};

function AssignTaskModal({ to, onClose, onSaved, users, currentUserId }) {
  const [form, setForm] = useState({
    text: "",
    priority: "normal",
    notes: "",
    due_date: "",
    assigned_to_user_id: to?.id || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.text.trim()) { toast.error("Task text required"); return; }
    setBusy(true);
    try {
      await api.post("/chat/messages", {
        to_user_id: to.id,
        text: "",
        task: {
          text: form.text,
          priority: form.priority,
          notes: form.notes,
          due_date: form.due_date || null,
          assigned_to_user_id: form.assigned_to_user_id || to.id,
        },
      });
      toast.success("Task sent");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-sm sm:rounded-sm w-full sm:max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="assign-task-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Send task to</div>
            <div className="font-heading font-black text-xl">{to.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">Task</label>
            <textarea
              className="input-lg min-h-[80px] py-2"
              placeholder="e.g. Deliver 5 HP 19kg to Radhe Dhokla by 10 AM"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="assign-task-text"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">Priority</label>
              <select className="input-lg" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="assign-task-priority">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="small-label block mb-1.5">Due date</label>
              <input type="date" className="input-lg" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="assign-task-due" />
            </div>
          </div>
          <div>
            <label className="small-label block mb-1.5">Notes (optional)</label>
            <textarea
              className="input-lg min-h-[60px] py-2"
              placeholder="Any context, address, contact number etc."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="assign-task-notes"
            />
          </div>
          <div>
            <label className="small-label block mb-1.5">Assign to</label>
            <select className="input-lg" value={form.assigned_to_user_id}
              onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })}
              data-testid="assign-task-assignee">
              {users.filter((u) => u.id !== currentUserId).map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </select>
            <div className="text-xs text-gray-500 mt-1">By default, the task is assigned to the person you&apos;re chatting with.</div>
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="assign-task-save">
            <Save size={16} /> {busy ? "Sending…" : "Send Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m, mine, adminView }) {
  const time = m.created_at ? format(parseISO(m.created_at), "HH:mm · dd MMM") : "";
  if (m.is_task && m.task) {
    const pr = PRIORITY_STYLE[m.task.priority] || PRIORITY_STYLE.normal;
    const done = m.task.status === "done";
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`}>
        <div className={`max-w-[85%] rounded-sm border ${done ? "bg-[#DCFCE7] border-[#86EFAC]" : "bg-[#FEF3C7] border-[#FDE68A]"} p-3`}>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck size={14} className={done ? "text-[#15803D]" : "text-[#B45309]"} />
            <span className="small-label">{done ? "Task done" : "Task"}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${pr.badge}`}>{pr.label}</span>
          </div>
          <div className={`font-semibold text-sm ${done ? "line-through text-gray-500" : ""}`}>{m.task.text}</div>
          {m.task.notes && <div className="text-xs text-gray-600 mt-1">{m.task.notes}</div>}
          <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-2 flex-wrap">
            <span>Assigned to <b>{m.task.assigned_to_name}</b></span>
            {m.task.due_date && <span>· Due {m.task.due_date}</span>}
            <span>· by {m.from_name}</span>
          </div>
          {done && (
            <div className="text-[11px] text-[#15803D] mt-1">Done by {m.task.done_by_name || "—"} {m.task.done_at ? `at ${format(parseISO(m.task.done_at), "HH:mm dd MMM")}` : ""}</div>
          )}
          <div className="text-[10px] text-gray-400 mt-1">{time}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[85%] rounded-sm px-3 py-2 ${mine ? "bg-black text-white" : "bg-white border border-gray-200"}`}>
        {adminView && !mine && (
          <div className={`text-[10px] font-bold uppercase mb-0.5 ${mine ? "text-gray-300" : "text-gray-500"}`}>{m.from_name}</div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
        <div className={`text-[10px] mt-1 ${mine ? "text-gray-400" : "text-gray-500"}`}>{time}</div>
      </div>
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // { thread_key, other, is_admin_view }
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const [{ data: u }, { data: t }] = await Promise.all([
        api.get("/chat/users"),
        api.get("/chat/threads"),
      ]);
      setUsers(u);
      setThreads(t);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadMessages = async (thread_key, other_id) => {
    try {
      const params = other_id ? { with_user_id: other_id } : { thread_key };
      const { data } = await api.get("/chat/messages", { params });
      setMessages(data.messages || []);
      setActive({
        thread_key: data.thread_key,
        other: data.other,
        is_admin_view: data.is_admin_view,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadThreads(); }, []);

  // Poll every 5s for messages of active thread + threads list
  useEffect(() => {
    if (!active) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/chat/messages", {
          params: active.thread_key ? { thread_key: active.thread_key } : {},
        });
        setMessages(data.messages || []);
      } catch { /* ignore polling errors */ }
    }, 5000);
    return () => clearInterval(id);
  }, [active?.thread_key]);

  useEffect(() => {
    const id = setInterval(() => { loadThreads(); }, 10000);
    return () => clearInterval(id);
  }, []);

  // Scroll to bottom when messages arrive
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const send = async () => {
    if (!active || !text.trim() || sending) return;
    if (active.is_admin_view) { toast.error("You are viewing another thread as admin"); return; }
    setSending(true);
    try {
      await api.post("/chat/messages", { to_user_id: active.other.id, text });
      setText("");
      await loadMessages(active.thread_key, active.other.id);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  // Users I can start a chat with (exclude self, exclude those already in threads)
  const startableUsers = useMemo(() => {
    const uid = user?.id;
    const existing = new Set(
      threads.filter((t) => t.other && t.is_own).map((t) => t.other.id)
    );
    return users.filter((u) => u.id !== uid && !existing.has(u.id));
  }, [users, threads, user]);

  return (
    <div className="space-y-4" data-testid="chat-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Team Chat</h2>
          <p className="text-xs text-gray-500 mt-1">
            {isAdmin
              ? <><ShieldCheck size={12} className="inline mb-0.5 mr-1" /> Admin view — you can see every conversation and chat 1-on-1.</>
              : <>1-on-1 messaging with team. Convert any message into a task.</>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[420px]">
        {/* Sidebar */}
        <div className={`card-flat overflow-hidden flex flex-col ${active ? "hidden md:flex" : "flex"}`} data-testid="chat-sidebar">
          <div className="p-3 border-b border-gray-200 flex items-center gap-2">
            <Users size={13} className="text-gray-500" />
            <span className="small-label">Threads</span>
            <span className="ml-auto text-[10px] text-gray-400">{threads.length}</span>
          </div>
          <div className="overflow-auto flex-1">
            {threads.map((t) => {
              const p = t.participants || [];
              const label = t.is_own && t.other
                ? t.other.name
                : `${p[0]?.name || "?"} ↔ ${p[1]?.name || "?"}`;
              const isReadOnly = !t.is_own;
              const last = t.last_message;
              const lastText = last?.is_task
                ? `📋 ${last.task?.text || ""}`
                : (last?.text || "");
              const timeAgo = last?.created_at
                ? formatDistanceToNow(parseISO(last.created_at), { addSuffix: true })
                : "";
              return (
                <button
                  key={t.thread_key}
                  onClick={() => loadMessages(t.thread_key, t.is_own ? t.other.id : null)}
                  className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 ${active?.thread_key === t.thread_key ? "bg-gray-100" : ""}`}
                  data-testid={`thread-${t.thread_key}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-sm truncate flex-1">{label}</div>
                    {t.unread_count > 0 && (
                      <span className="bg-[#B91C1C] text-white text-[10px] px-1.5 py-0.5 rounded-sm font-bold">{t.unread_count}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">{lastText || <span className="italic text-gray-400">no messages</span>}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                    {isReadOnly && <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-sm font-bold">Admin view</span>}
                    <span>{timeAgo}</span>
                  </div>
                </button>
              );
            })}
            {threads.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-400">No threads yet. Pick someone below to start.</div>
            )}
          </div>
          {startableUsers.length > 0 && (
            <div className="border-t border-gray-200 max-h-56 overflow-auto" data-testid="startable-users">
              <div className="p-3 pb-1 small-label text-gray-500">Start a new chat</div>
              {startableUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => loadMessages(null, u.id)}
                  className="w-full text-left px-3 py-2 border-t border-gray-100 hover:bg-gray-50 flex items-center gap-2"
                  data-testid={`start-chat-${u.id}`}
                >
                  <span className="w-7 h-7 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center">
                    {(u.name || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold flex-1 truncate">{u.name}</span>
                  <span className="text-[10px] text-gray-500 uppercase">{u.role}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Chat area */}
        <div className={`card-flat overflow-hidden flex flex-col ${!active ? "hidden md:flex" : "flex"}`} data-testid="chat-area">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <MessageSquare size={40} className="text-gray-300 mx-auto mb-2" />
                <div className="text-sm text-gray-500">Pick a thread or start a new chat</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-3 border-b border-gray-200 flex items-center gap-2 sticky top-0 bg-white z-10">
                <button onClick={() => setActive(null)} className="md:hidden p-1 hover:bg-gray-100 rounded-sm" data-testid="chat-back">
                  <ChevronLeft size={18} />
                </button>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{active.other?.name || "Chat"}</div>
                  <div className="text-[11px] text-gray-500 uppercase">{active.other?.role}</div>
                </div>
                {active.is_admin_view && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-sm bg-gray-100 text-gray-600 font-bold uppercase">
                    <ShieldCheck size={10} className="inline mb-0.5 mr-1" /> Admin view · read-only
                  </span>
                )}
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-auto p-3 bg-[#F9FAFB]" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-gray-400 py-10">Send the first message.</div>
                )}
                {messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    m={m}
                    mine={m.from_user_id === user.id}
                    adminView={active.is_admin_view}
                  />
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              {!active.is_admin_view && (
                <div className="p-3 border-t border-gray-200 flex items-end gap-2" data-testid="chat-composer">
                  <button
                    onClick={() => setAssignFor(active.other)}
                    className="h-10 px-3 border border-gray-300 rounded-sm text-xs font-semibold flex items-center gap-1 hover:bg-gray-50"
                    title="Assign as task"
                    data-testid="chat-assign-task"
                  >
                    <ClipboardCheck size={13} /> Task
                  </button>
                  <textarea
                    className="input-lg min-h-[40px] max-h-[120px] py-2 flex-1"
                    placeholder="Type a message…"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    data-testid="chat-text-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !text.trim()}
                    className="btn-primary h-10 px-4 flex items-center gap-1.5"
                    data-testid="chat-send"
                  >
                    <Send size={14} /> {sending ? "…" : "Send"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {assignFor && (
        <AssignTaskModal
          to={assignFor}
          users={users}
          currentUserId={user?.id}
          onClose={() => setAssignFor(null)}
          onSaved={() => {
            setAssignFor(null);
            if (active) loadMessages(active.thread_key, active.other.id);
            loadThreads();
            toast.info("Task added to Pending Works");
          }}
        />
      )}
    </div>
  );
}
